import { DataPage } from "@/app.routes";
import { UserHelper } from "@/helpers/user.helper";
import { Injectable } from "@angular/core";
import {
  RealProperty,
  SharingDataCategory,
  SharingScope,
} from "@eemo/api-client";
import { DeviceDetectorService } from "ngx-device-detector";
import { BehaviorSubject, Observable } from "rxjs";
import { coreEnvironment } from "src/environments/core/core-environment";
import { NavEnv } from "src/environments/core/nav.environment";
import { AppConfigService } from "./app-config.service";
import { MenuToNative, MenuToNativeId } from "./bridge/bridge.service";

@Injectable({
  providedIn: "root",
})
export class NavService {
  public mainMenuOpenedSubject: BehaviorSubject<boolean>;
  public mainMenuOpened: Observable<boolean>;

  public menuItems$ = new BehaviorSubject<MenuToNative[]>([]);

  currentProp: RealProperty | null;

  currentDataPage$: BehaviorSubject<DataPage> = new BehaviorSubject(null);

  constructor(
    private deviceService: DeviceDetectorService,
    private appConfigService: AppConfigService,
    public userHelper: UserHelper
  ) {
    // the nav menu is closed by default on mobile device
    this.mainMenuOpenedSubject = new BehaviorSubject<boolean>(
      coreEnvironment.menuOpen
    );
    this.mainMenuOpened = this.mainMenuOpenedSubject.asObservable();

    this.appConfigService.selectedPropObs.subscribe((p) => {
      this.currentProp = p;
    });
  }

  openMainMenu() {
    this.mainMenuOpenedSubject.next(true);
  }
  openMainMenuNotMobile() {
    if (!this.deviceService.isMobile()) {
      this.openMainMenu();
    }
  }

  toggleMainMenu() {
    this.mainMenuOpenedSubject.next(!this.mainMenuOpenedSubject.getValue());
  }

  menuHandler(
    dropBoxMenuAvailable: boolean,
    dropBoxCount: number,
    sharingRequestCount: number,
    connectorsMenuAvailable: boolean,
    contactsMenuAvailable: boolean,
    kpiMenuAvailable: boolean,
    adminMenuAvailable: boolean,
    saleMenuAvailable: boolean,
    canUpdateProperty: boolean,
    emailValidated: boolean,
    rescueMode: boolean,
    currentUid: string | null
  ) {
    this.menuItems$.next(
      this.genMenuItems(
        dropBoxMenuAvailable,
        dropBoxCount,
        sharingRequestCount,
        connectorsMenuAvailable,
        contactsMenuAvailable,
        kpiMenuAvailable,
        adminMenuAvailable,
        saleMenuAvailable,
        canUpdateProperty,
        emailValidated,
        rescueMode,
        currentUid
      )
    );
  }

  genMenuItems(
    dropBoxMenuAvailable: boolean,
    dropBoxCount: number,
    sharingRequestCount: number,
    connectorsMenuAvailable: boolean,
    contactsMenuAvailable: boolean,
    kpiMenuAvailable: boolean,
    adminMenuAvailable: boolean,
    saleMenuAvailable: boolean,
    canUpdateProperty: boolean,
    emailValidated: boolean,
    rescueMode: boolean,
    currentUid: string | null
  ): MenuToNative[] {
    const menu: MenuToNative[] = [];

    // property
    const property = this.getNavEnvById(MenuToNativeId.HOME_PROPERTY);
    if (this.currentProp != null) {
      const bookletCount = this.currentProp.details.fillingCount.booklet;
      const docCount = this.currentProp.details.fillingCount.docs;
      const photoCount = this.currentProp.details.fillingCount.photos;
      const sharingCount = this.currentProp.details.fillingCount.sharings;
      const servicesCount = this.currentProp.details.fillingCount.services;
      const financialReportCount =
        this.currentProp.details.fillingCount.financialReport;
      const zoningCount = this.currentProp.details.fillingCount.zoningCount;

      const locationCount = this.getPropCategoryCount(
        SharingDataCategory.LOCATION
      );
      const buildingCount = this.getPropCategoryCount(
        SharingDataCategory.BUILDING
      );
      const energyCount = this.getPropCategoryCount(SharingDataCategory.ENERGY);
      const equipmentCount = this.getPropCategoryCount(
        SharingDataCategory.EQUIPMENT
      );
      const financialCount = this.getPropCategoryCount(
        SharingDataCategory.FINANCIAL
      );
      const peopleCount = this.getPropCategoryCount(SharingDataCategory.PEOPLE);
      const condoCount = this.getPropCategoryCount(
        SharingDataCategory.CONDOMINIUM
      );
      const infoCount =
        locationCount +
        buildingCount +
        energyCount +
        +equipmentCount +
        financialCount +
        peopleCount +
        condoCount +
        zoningCount;

      const timelineCount = this.getPropCategoryCount(
        SharingDataCategory.TIMELINE
      );
      const consumptionCount = this.getPropCategoryCount(
        SharingDataCategory.CONSUMPTION
      );
      const maintenanceCount = this.getPropCategoryCount(
        SharingDataCategory.MAINTENANCE
      );
      const workCount = this.getPropCategoryCount(SharingDataCategory.WORK);
      const trackingCount =
        timelineCount +
        consumptionCount +
        maintenanceCount +
        workCount +
        financialReportCount;

      const isViewSharing =
        SharingScope.VIEWING === this.currentProp.details.accessSharing?.scope;

      // --- booklet
      const booklet = property.childs.find(
        (c) => c.id === MenuToNativeId.BOOKLET
      );
      booklet.kpi = bookletCount;
      booklet.removed = isViewSharing && bookletCount === 0;
      this.addUidBeforeLink(currentUid, booklet);
      // --- infos
      const infos = property.childs.find(
        (c) => c.id === MenuToNativeId.PROP_INFO
      );
      infos.kpi = infoCount;
      infos.removed = isViewSharing && infoCount === 0;
      this.addUidBeforeLink(currentUid, infos);
      infos.childs.forEach((c) => {
        let currentKpi = 0;
        switch (c.id) {
          case MenuToNativeId.LOCATION:
            currentKpi = locationCount;
            break;
          case MenuToNativeId.BUILDING:
            currentKpi = buildingCount;
            break;
          case MenuToNativeId.ENERGY:
            currentKpi = energyCount;
            break;
          case MenuToNativeId.EQUIPMENT:
            currentKpi = equipmentCount;
            break;
          case MenuToNativeId.FINANCIAL:
            currentKpi = financialCount;
            break;
          case MenuToNativeId.PEOPLE:
            currentKpi = peopleCount;
            break;
          case MenuToNativeId.CONDOMINIUM:
            currentKpi = condoCount;
            break;
        }
        c.kpi = currentKpi;
        c.removed = isViewSharing && currentKpi === 0;
        this.addUidBeforeLink(currentUid, c);
      });
      // condo view
      const condoMenu = infos.childs.find(
        (c) => c.id === MenuToNativeId.CONDOMINIUM
      );
      condoMenu.removed =
        condoMenu.removed || !this.currentProp.details.isInCondo;
      // --- tracking
      const tracking = property.childs.find(
        (c) => c.id === MenuToNativeId.TRACKING
      );
      tracking.kpi = trackingCount;
      tracking.removed = isViewSharing && trackingCount === 0;
      this.addUidBeforeLink(currentUid, tracking);
      tracking.childs.forEach((c) => {
        let currentKpi = 0;
        switch (c.id) {
          case MenuToNativeId.TIMELINE:
            currentKpi = timelineCount;
            break;
          case MenuToNativeId.CONSUMPTION:
            currentKpi = consumptionCount;
            break;
          case MenuToNativeId.MAINTENANCE:
            currentKpi = maintenanceCount;
            break;
          case MenuToNativeId.WORK:
            currentKpi = workCount;
            break;
          case MenuToNativeId.TRACKING_FINANCIAL:
            currentKpi = financialReportCount;
            break;
        }
        c.kpi = currentKpi;
        c.removed =
          !this.userHelper.canViewCategoryByMenuId(this.currentProp, c.id) ||
          (isViewSharing && currentKpi === 0);
        this.addUidBeforeLink(currentUid, c);
      });
      // --- photos
      const photos = property.childs.find((c) => c.id === MenuToNativeId.PHOTO);
      photos.kpi = photoCount;
      photos.removed = !this.userHelper.canViewCategoryByMenuId(
        this.currentProp,
        photos.id
      );
      this.addUidBeforeLink(currentUid, photos);
      // --- vault
      const vault = property.childs.find((c) => c.id === MenuToNativeId.VAULT);
      vault.kpi = docCount;
      this.addUidBeforeLink(currentUid, vault);
      vault.removed = isViewSharing && docCount === 0;
      // --- shares
      const shares = property.childs.find(
        (c) => c.id === MenuToNativeId.PROP_SHARES
      );
      shares.kpi = sharingCount;
      this.addUidBeforeLink(currentUid, shares);
      shares.removed = !this.userHelper.canViewCategoryByMenuId(
        this.currentProp,
        shares.id
      );
      // --- sale
      const sale = property.childs.find(
        (c) => c.id === MenuToNativeId.PROP_SALE
      );
      this.addUidBeforeLink(currentUid, sale);
      sale.removed = !saleMenuAvailable;
      // --- services
      const services = property.childs.find(
        (c) => c.id === MenuToNativeId.PROP_SERVICES
      );
      services.kpi = servicesCount;
      this.addUidBeforeLink(currentUid, services);
      // FIXME we don't know really if we have to show services or not
      services.removed = !canUpdateProperty;
    }
    menu.push(property);
    // dropbox
    if (dropBoxMenuAvailable) {
      const dropbox: MenuToNative = this.getNavEnvById(MenuToNativeId.DROPBOX);
      if (dropBoxCount && dropBoxCount > 0) {
        dropbox.chip = dropBoxCount + "";
        dropbox.chipText = "Des documents sont en attente de validation";
      } else {
        dropbox.chip = "";
        dropbox.chipText = "";
      }
      menu.push(dropbox);
    }
    // shares
    const shares = this.getNavEnvById(MenuToNativeId.SHARES);
    if (sharingRequestCount && sharingRequestCount > 0) {
      shares.chip = sharingRequestCount + "";
      shares.chipText = "Des demandes d'accès sont en attente de validation";
    } else {
      shares.chip = "";
      shares.chipText = "";
    }
    menu.push(shares);
    // connectors
    if (connectorsMenuAvailable) {
      menu.push(this.getNavEnvById(MenuToNativeId.CONNECTORS));
    }
    // contacts
    if (contactsMenuAvailable) {
      menu.push(this.getNavEnvById(MenuToNativeId.CONTACTS));
    }
    // kpis
    if (kpiMenuAvailable) {
      menu.push(this.getNavEnvById(MenuToNativeId.KPIS));
    }
    // administration
    if (adminMenuAvailable) {
      menu.push(this.getNavEnvById(MenuToNativeId.ADMIN));
    }
    // profile
    const profile: MenuToNative = this.getNavEnvById(MenuToNativeId.PROFILE);
    if (!emailValidated) {
      profile.chip = "!";
      profile.chipText = "Votre adresse email n'est pas vérifiée";
    } else {
      profile.chip = "";
      profile.chipText = "";
    }
    menu.push(profile);
    // logout
    menu.push(this.getNavEnvById(MenuToNativeId.LOGOUT));
    return menu;
  }

  getPropCategoryCount(category: SharingDataCategory): number {
    return this.currentProp.details.fillingCount.categories[category]
      ? this.currentProp.details.fillingCount.categories[category]
      : 0;
  }

  getNavEnvById(id: MenuToNativeId): MenuToNative | null {
    let find: MenuToNative | null = null;
    const newEnv = this.getNavEnv();
    find = this.searchNavEnv(id, newEnv);
    return find;
  }

  getNavEnv() {
    return [...NavEnv];
  }

  searchNavEnv(id: MenuToNativeId, list: MenuToNative[]): MenuToNative | null {
    let found: MenuToNative | null = null;
    list?.forEach((m) => {
      if (m.id === id) {
        found = m;
      } else if (!found) {
        found = this.searchNavEnv(id, m.childs);
      }
    });
    return found;
  }

  updateMenuList(list: MenuToNative[], newMenu: MenuToNative) {
    list[list.findIndex((l) => l.id === newMenu.id)] = newMenu;
    return list;
  }

  updateMenuSubTitle(list: MenuToNative[], id: MenuToNativeId, title: string) {
    const newMenu = list.find((l) => l.id === id);
    newMenu.subTitle = title;
    return this.updateMenuList(list, newMenu);
  }

  addUidBeforeLink(currentUid: string | null, item: MenuToNative) {
    const propertyStartPath = "/property/" + currentUid;

    if (currentUid && item.id === MenuToNativeId.PROP_INFO) {
      item.link = propertyStartPath + "/infos";
    } else if (currentUid && item.id === MenuToNativeId.FICHE_SECOURS) {
      item.link = propertyStartPath + "/rescue";
    } else if (currentUid && item.id === MenuToNativeId.BOOKLET) {
      item.link = propertyStartPath + "/booklet";
    } else if (currentUid && item.id === MenuToNativeId.LOCATION) {
      item.link = propertyStartPath + "/location";
    } else if (currentUid && item.id === MenuToNativeId.BUILDING) {
      item.link = propertyStartPath + "/building";
    } else if (currentUid && item.id === MenuToNativeId.ENERGY) {
      item.link = propertyStartPath + "/energy";
    } else if (currentUid && item.id === MenuToNativeId.FINANCIAL) {
      item.link = propertyStartPath + "/financial";
    } else if (currentUid && item.id === MenuToNativeId.PEOPLE) {
      item.link = propertyStartPath + "/people";
    } else if (currentUid && item.id === MenuToNativeId.CONDOMINIUM) {
      item.link = propertyStartPath + "/condo";
    } else if (currentUid && item.id === MenuToNativeId.TRACKING) {
      item.link = propertyStartPath + "/tracking";
    } else if (currentUid && item.id === MenuToNativeId.TIMELINE) {
      item.link = propertyStartPath + "/timeline";
    } else if (currentUid && item.id === MenuToNativeId.CONSUMPTION) {
      item.link = propertyStartPath + "/consumption";
    } else if (currentUid && item.id === MenuToNativeId.MAINTENANCE) {
      item.link = propertyStartPath + "/maintenance";
    } else if (currentUid && item.id === MenuToNativeId.WORK) {
      item.link = propertyStartPath + "/work";
    } else if (currentUid && item.id === MenuToNativeId.TRACKING_FINANCIAL) {
      item.link = propertyStartPath + "/tracking-financial";
    } else if (currentUid && item.id === MenuToNativeId.PHOTO) {
      item.link = propertyStartPath + "/photos";
    } else if (currentUid && item.id === MenuToNativeId.VAULT) {
      item.link = propertyStartPath + "/vault";
    } else if (currentUid && item.id === MenuToNativeId.EQUIPMENT) {
      item.link = propertyStartPath + "/equipments";
    } else if (currentUid && item.id === MenuToNativeId.PROP_SHARES) {
      item.link = propertyStartPath + "/shares";
    } else if (currentUid && item.id === MenuToNativeId.PROP_SERVICES) {
      item.link = propertyStartPath + "/services";
    } else if (currentUid && item.id === MenuToNativeId.PROP_SALE) {
      item.link = propertyStartPath + "/sale";
    }
  }

  onNavUidEvent(uid: string | null) {
    let dataPage = this.currentDataPage$.getValue();
    if (!dataPage) {
      dataPage = {
        level: null,
        page: null,
        uid: null,
      };
    }
    dataPage.uid = uid;
    this.currentDataPage$.next(dataPage);
  }
}
